import { getCalculateBucketFunction } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTerritoryGroup.helpers';
import {
  type BoundaryFillConfig, type BoundaryFillDemographicConfig, type BoundaryFillDensityConfig,
  type BoundaryFillGroupConfig, type BoundaryFillManualConfig, type BoundaryFillNumericConfig,
  isBoundaryFillDemographicConfig, isBoundaryFillDensityConfig, isBoundaryFillGroupConfig, isBoundaryFillManualConfig,
  isBoundaryFillNumericConfig,
} from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/extraFills.types';
import {
  BoundaryTerritoryType, type BTGWithActiveExtraFill,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type {
  BoundaryTerritory, BoundaryTerritoryGroup, BoundaryTerritoryGroupSettings,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

export const createBoundaryFillConfig = (desiredConfig: MaybePartial<BoundaryFillConfig>): BoundaryFillConfig | null => {
  switch (desiredConfig.boundaryTerritoryType) {
    case BoundaryTerritoryType.Groups: {
      if (desiredConfig.boundaryFillId && desiredConfig.boundaryFillData && desiredConfig.style) {
        return {
          ...desiredConfig,
          boundaryFillData: desiredConfig.boundaryFillData,
          boundaryFillId: desiredConfig.boundaryFillId,
          boundaryTerritoryType: desiredConfig.boundaryTerritoryType,
          style: desiredConfig.style,
        } satisfies BoundaryFillGroupConfig;
      }
      return null;
    }
    case BoundaryTerritoryType.Numeric: {
      if (desiredConfig.boundaryFillId && desiredConfig.boundaryFillData && desiredConfig.style
        && desiredConfig.rangesCount && desiredConfig.numericCalculateBucketFunction) {
        return {
          ...desiredConfig,
          boundaryFillData: desiredConfig.boundaryFillData,
          boundaryFillId: desiredConfig.boundaryFillId,
          boundaryTerritoryType: desiredConfig.boundaryTerritoryType,
          bucketType: desiredConfig.bucketType ?? 'value-range',
          numericCalculateBucketFunction: desiredConfig.numericCalculateBucketFunction,
          rangesCount: desiredConfig.rangesCount,
          style: desiredConfig.style,
        } satisfies BoundaryFillNumericConfig;
      }
      return null;
    }
    case BoundaryTerritoryType.Demographic: {
      if (desiredConfig.boundaryFillId && desiredConfig.style && desiredConfig.selectedDemographicId && desiredConfig.rangesCount) {
        return {
          ...desiredConfig,
          boundaryFillId: desiredConfig.boundaryFillId,
          boundaryTerritoryType: desiredConfig.boundaryTerritoryType,
          bucketType: desiredConfig.bucketType ?? 'value-range',
          rangesCount: desiredConfig.rangesCount,
          selectedDemographicId: desiredConfig.selectedDemographicId,
          style: desiredConfig.style,
        } satisfies BoundaryFillDemographicConfig;
      }
      return null;
    }
    case BoundaryTerritoryType.Manual: {
      if (desiredConfig.boundaryFillId && desiredConfig.style) {
        return {
          ...desiredConfig,
          boundaryFillId: desiredConfig.boundaryFillId,
          boundaryTerritoryType: desiredConfig.boundaryTerritoryType,
          style: desiredConfig.style,
        } satisfies BoundaryFillManualConfig;
      }
      return null;
    }
    case BoundaryTerritoryType.Density: {
      if (desiredConfig.boundaryFillId && desiredConfig.style && desiredConfig.rangesCount) {
        return {
          ...desiredConfig,
          boundaryFillId: desiredConfig.boundaryFillId,
          boundaryTerritoryType: desiredConfig.boundaryTerritoryType,
          bucketType: desiredConfig.bucketType ?? 'value-range',
          rangesCount: desiredConfig.rangesCount,
          style: desiredConfig.style,
        } satisfies BoundaryFillDensityConfig;
      }
      return null;
    }
    default: {
      return null;
    }
  }
};

export const convertBoundaryFillSettingsToBTGSettingsOverride = (boundaryFillConfig?: BoundaryFillConfig): Partial<BoundaryTerritoryGroupSettings> => {
  if (!boundaryFillConfig) {
    return {};
  }
  const commonProps = boundaryFillConfig.style ? { style: boundaryFillConfig.style } : {};

  if (isBoundaryFillGroupConfig(boundaryFillConfig)) {
    return {
      ...commonProps,
      boundaryTerritoryType: boundaryFillConfig.boundaryTerritoryType,
      bucketColumnId: boundaryFillConfig.boundaryFillData.columnId,
      bucketType: 'value',
      calculateBucketFunction: getCalculateBucketFunction(boundaryFillConfig.boundaryTerritoryType),
    };
  }
  if (isBoundaryFillNumericConfig(boundaryFillConfig)) {
    return {
      ...commonProps,
      boundaryTerritoryType: boundaryFillConfig.boundaryTerritoryType,
      bucketColumnId: boundaryFillConfig.boundaryFillData.columnId,
      bucketType: boundaryFillConfig.bucketType,
      calculateBucketFunction: getCalculateBucketFunction(
        boundaryFillConfig.boundaryTerritoryType,
        boundaryFillConfig.numericCalculateBucketFunction,
      ),
      rangesCount: boundaryFillConfig.rangesCount,
    };
  }
  if (isBoundaryFillDemographicConfig(boundaryFillConfig)) {
    return {
      ...commonProps,
      boundaryTerritoryType: boundaryFillConfig.boundaryTerritoryType,
      bucketType: boundaryFillConfig.bucketType,
      demographicId: boundaryFillConfig.selectedDemographicId,
      calculateBucketFunction: getCalculateBucketFunction(boundaryFillConfig.boundaryTerritoryType),
      rangesCount: boundaryFillConfig.rangesCount,
    };
  }
  if (isBoundaryFillDensityConfig(boundaryFillConfig)) {
    return {
      ...commonProps,
      boundaryTerritoryType: boundaryFillConfig.boundaryTerritoryType,
      bucketType: boundaryFillConfig.bucketType,
      calculateBucketFunction: getCalculateBucketFunction(boundaryFillConfig.boundaryTerritoryType),
      rangesCount: boundaryFillConfig.rangesCount,
    };
  }
  if (isBoundaryFillManualConfig(boundaryFillConfig)) {
    return {
      ...commonProps,
      boundaryTerritoryType: boundaryFillConfig.boundaryTerritoryType,
      bucketType: null,
      calculateBucketFunction: getCalculateBucketFunction(boundaryFillConfig.boundaryTerritoryType),
    };
  }

  return {};
};

const getBoundaryTerritoriesOverride = (groupInfo: PatchableBTGWithActiveExtraFill): readonly BoundaryTerritory[] | undefined => {
  const { activeFillInfo } = groupInfo;
  if (activeFillInfo && !activeFillInfo.btgDetails) {
    return undefined;
  }
  if (activeFillInfo && activeFillInfo.btgDetails) {
    return activeFillInfo.btgDetails.boundaryTerritories;
  }
  return [];
};

type PatchableBTGWithActiveExtraFill = Omit<BTGWithActiveExtraFill, 'activeFillInfo'> & {
  readonly activeFillInfo: Omit<BTGWithActiveExtraFill['activeFillInfo'], 'btgDetails'> & {
    readonly btgDetails?: {
      readonly boundaryTerritories: readonly BoundaryTerritory[];
    };
  };
} | BTGWithActiveExtraFill;

export const patchActiveFillIntoBTG = (groupInfo: PatchableBTGWithActiveExtraFill): BoundaryTerritoryGroup => {
  const { activeFillConfig, boundaryTerritoryGroup } = groupInfo;
  const boundaryTerritories = getBoundaryTerritoriesOverride(groupInfo);

  const result = {
    ...boundaryTerritoryGroup,
    settings: {
      ...boundaryTerritoryGroup.settings,
      ...convertBoundaryFillSettingsToBTGSettingsOverride(activeFillConfig),
      ...(boundaryTerritories ? { boundaryTerritories } : {}),
    },
  };

  return result;
};
